import dayjs, { type Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchWearableObservationEntries } from "./fetchWearableObservationEntries";
import {
  EXERCISE_SESSION_CODE,
  getObservationPrimaryCode,
  getSectionDeviceName,
  getWearableObservationDayKey,
  getWearableObservationSectionId,
  type WearableObservationSectionId,
} from "@/utils/wearableObservationFormat";
import { sortObservationEntries } from "./metricDisplayOrder";

export interface IssuedGroup {
  time: string;
  items: any[];
}

/**
 * Data + agrupamento das Observations de dispositivos por período/horário/seção.
 * Sempre busca via GetObservation no intervalo (1 / 7 / 15 / 30 dias).
 * patientId = CPF do paciente (página do paciente ou atendimento).
 */
export function useWearableObservationsData(
  patientId: string | undefined,
  periodStart: Dayjs | undefined,
  periodEnd: Dayjs | undefined,
  /** Em períodos multi-dia, os chips de horário de envio não se aplicam. */
  rangeDays = 1
) {
  const [loading, setLoading] = useState(false);
  const [observationEntries, setObservationEntries] = useState<any[]>([]);
  const [selectedIssuedTime, setSelectedIssuedTime] = useState<string | "all">(
    "all"
  );
  /** Incrementado pelo botão refresh para forçar novo GetObservation. */
  const [refreshKey, setRefreshKey] = useState(0);

  const periodStartKey = periodStart?.isValid()
    ? periodStart.format("YYYY-MM-DD")
    : "";
  const periodEndKey = periodEnd?.isValid()
    ? periodEnd.format("YYYY-MM-DD")
    : "";

  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchObservations() {
      if (!patientId || !periodStartKey || !periodEndKey) {
        setObservationEntries([]);
        return;
      }

      try {
        setLoading(true);
        // Busca por período (30 dias = 2x15 — ver fetchWearableObservationEntries)
        const entries = await fetchWearableObservationEntries({
          patientId,
          periodStartKey,
          periodEndKey,
          rangeDays,
        });
        if (cancelled) return;
        setObservationEntries(entries);
      } catch {
        if (!cancelled) setObservationEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchObservations();
    return () => {
      cancelled = true;
    };
  }, [patientId, periodStartKey, periodEndKey, refreshKey, rangeDays]);

  const filteredObservations = useMemo(() => {
    if (
      !periodStart ||
      !periodEnd ||
      !dayjs(periodStart).isValid() ||
      !dayjs(periodEnd).isValid()
    ) {
      return [];
    }

    const startKey = dayjs(periodStart).format("YYYY-MM-DD");
    const endKey = dayjs(periodEnd).format("YYYY-MM-DD");

    return observationEntries
      .filter((entry: any) => {
        const r = entry?.resource;
        // Dia canônico (sono = noite do start; tela = fim do uso).
        // Sem lookback na exibição: o lookback é só na busca MHD.
        // Evita sono de 01/08 aparecer no chip de tempo de tela de 02/08
        // (mesmo HH:mm de issued em dias diferentes).
        const dayKey = getWearableObservationDayKey(r);
        if (!dayKey) return false;
        return dayKey >= startKey && dayKey <= endKey;
      })
      .sort((a: any, b: any) => {
        const getSortDate = (entry: any) => {
          const r = entry?.resource;
          if (r?.issued) return dayjs(r.issued).valueOf();
          if (r?.effectiveDateTime) return dayjs(r.effectiveDateTime).valueOf();
          if (r?.effectivePeriod?.start)
            return dayjs(r.effectivePeriod.start).valueOf();
          return dayjs(r?.meta?.lastUpdated).valueOf() || 0;
        };
        return getSortDate(b) - getSortDate(a);
      });
  }, [observationEntries, periodStart, periodEnd]);

  // Agrupa por horário só no dia único — em intervalo o mesmo HH:mm mistura dias
  const issuedGroups = useMemo<IssuedGroup[]>(() => {
    if (rangeDays !== 1) return [];

    const groups: Record<string, any[]> = {};

    filteredObservations.forEach((entry: any) => {
      const issued = entry?.resource?.issued;
      if (!issued) return;

      // Só Observations cujo dia canônico é o dia selecionado já passaram
      // no filtro acima — o chip HH:mm não mistura mais outro dia.
      const timeKey = dayjs(issued).format("HH:mm");
      if (!groups[timeKey]) groups[timeKey] = [];
      groups[timeKey].push(entry);
    });

    return Object.entries(groups)
      .map(([time, items]) => ({ time, items }))
      .sort((a, b) => dayjs(b.time, "HH:mm").diff(dayjs(a.time, "HH:mm")));
  }, [filteredObservations, rangeDays]);

  // "Todos" por padrão — o último horário (ex.: só água) escondia passos do 2º envio
  useEffect(() => {
    setSelectedIssuedTime("all");
  }, [issuedGroups]);

  /** Em "Todos": último valor por código; exercício = último lote do dia. */
  const observationsMergedLatestPerMetric = useMemo(() => {
    const bestIssuedByCode = new Map<string, number>();
    const bestBatchByExerciseDay = new Map<string, string>();
    const bestExerciseIssuedByDay = new Map<string, number>();

    filteredObservations.forEach((entry: any) => {
      const resource = entry?.resource;
      const code = getObservationPrimaryCode(resource) || "unknown";
      const issued = resource?.issued
        ? dayjs(resource.issued).valueOf()
        : dayjs(resource?.meta?.lastUpdated).valueOf() || 0;

      if (code === EXERCISE_SESSION_CODE) {
        const effective =
          resource?.effectivePeriod?.start ??
          resource?.effectiveDateTime ??
          resource?.issued;
        const dateKey = dayjs(effective).isValid()
          ? dayjs(effective).format("YYYY-MM-DD")
          : "";
        if (!dateKey) return;
        const current = bestExerciseIssuedByDay.get(dateKey) ?? 0;
        if (issued > current) {
          bestExerciseIssuedByDay.set(dateKey, issued);
          const batch = resource?.issued
            ? dayjs(resource.issued).format("YYYY-MM-DD HH:mm")
            : "";
          if (batch) bestBatchByExerciseDay.set(dateKey, batch);
        }
        return;
      }

      const current = bestIssuedByCode.get(code) ?? 0;
      if (issued >= current) bestIssuedByCode.set(code, issued);
    });

    return filteredObservations.filter((entry: any) => {
      const resource = entry?.resource;
      const code = getObservationPrimaryCode(resource) || "unknown";
      const issued = resource?.issued
        ? dayjs(resource.issued).valueOf()
        : dayjs(resource?.meta?.lastUpdated).valueOf() || 0;

      if (code === EXERCISE_SESSION_CODE) {
        const effective =
          resource?.effectivePeriod?.start ??
          resource?.effectiveDateTime ??
          resource?.issued;
        const dateKey = dayjs(effective).isValid()
          ? dayjs(effective).format("YYYY-MM-DD")
          : "";
        const bestBatch = bestBatchByExerciseDay.get(dateKey);
        if (!bestBatch) {
          return issued === (bestExerciseIssuedByDay.get(dateKey) ?? -1);
        }
        const batch = resource?.issued
          ? dayjs(resource.issued).format("YYYY-MM-DD HH:mm")
          : "";
        return batch === bestBatch;
      }

      return issued === (bestIssuedByCode.get(code) ?? -1);
    });
  }, [filteredObservations]);

  const observationsForSelectedTime = useMemo(() => {
    if (rangeDays !== 1) {
      return filteredObservations;
    }
    if (selectedIssuedTime === "all") {
      return observationsMergedLatestPerMetric;
    }
    const group = issuedGroups.find((g) => g.time === selectedIssuedTime);
    return group?.items ?? [];
  }, [
    filteredObservations,
    issuedGroups,
    selectedIssuedTime,
    rangeDays,
    observationsMergedLatestPerMetric,
  ]);

  const sections = useMemo(() => {
    const buckets: Record<WearableObservationSectionId, any[]> = {
      exercise: [],
      bodyComposition: [],
      screenTime: [],
      general: [],
      sleep: [],
    };

    observationsForSelectedTime.forEach((entry: any) => {
      buckets[getWearableObservationSectionId(entry?.resource)].push(entry);
    });

    // Ordem canônica por seção (Passos→…, sono, composição…)
    (Object.keys(buckets) as WearableObservationSectionId[]).forEach(
      (sectionId) => {
        buckets[sectionId] = sortObservationEntries(
          buckets[sectionId],
          sectionId
        );
      }
    );

    return { buckets, deviceNameOf: getSectionDeviceName };
  }, [observationsForSelectedTime]);

  return {
    loading,
    filteredObservations,
    issuedGroups,
    selectedIssuedTime,
    setSelectedIssuedTime,
    buckets: sections.buckets,
    deviceNameOf: sections.deviceNameOf,
    /** Refaz GetObservation do período atual (botão refresh). */
    refetch,
  };
}
